import createCachedSelector from 're-reselect';
import { createSelector } from '@reduxjs/toolkit';

import { ReduxState } from 'app/redux/types';

import { StakeType } from 'app/lib/staking/constants';

import {
  AccountType,
  AmountInputModifier,
  AmountValidationResult,
  OperatorRefusedCode,
  StakeErrorCode,
} from '../../../constants';
import {
  EntryAmount,
  EntryAmountSelector,
  RecordOfBetInfoType,
  RecordOfEntryAmountType,
  RecordOfEntryErrorType,
} from '../../../records';
import { RecordOfSingleStakeType } from '../../../types/stakeTypes/singles';
import { parseAmount, roundAmount } from '../../../utils/parsing';
import { selectAvailableStakeTypes, selectCheckedStakeType } from '../../stakeTypes';
import { selectEntriesState, selectSingleStakeTypeState, selectStakeTypesState } from '../../state';

// ---

export const selectSingleBetsInfo = (state: ReduxState): RecordOfSingleStakeType['betsInfo'] => {
  return selectSingleStakeTypeState(state).betsInfo;
};

export const selectSingleAmountModifier = (state: ReduxState): RecordOfSingleStakeType['amountModifier'] => {
  return selectSingleStakeTypeState(state).amountModifier;
};

export const selectBetInfoSubmissionByBetId = createCachedSelector(
  (state: ReduxState, betId: number) => selectSingleBetsInfo(state).get(betId),
  (betInfo) => {
    return betInfo?.submission;
  },
)((_state: ReduxState, betId: number) => betId);

export const isBetCanBeRestored = (error: RecordOfEntryErrorType | null) => {
  const code = error?.code;
  const reason = error?.rejectReason?.reason;
  if (!error) {
    return false;
  }

  return reason === OperatorRefusedCode.MATCH_SITUATION_CHANGED
         || reason === OperatorRefusedCode.BAD_PRICE
         || code === StakeErrorCode.BID_DECLINED;
};

export const selectSingleErrorsBetIds = createSelector(
  selectSingleBetsInfo,
  (betsInfo) => {
    return betsInfo.reduce((betsInfoErrors, betInfo) => {
      if (isBetCanBeRestored(betInfo.error)) {
        betsInfoErrors.push(betInfo.id);
      }

      return betsInfoErrors;
    }, [] as number[]);
  },
);

export const isOnlyAcceptAmount = (error?: RecordOfEntryErrorType | null) => {
  if (!error) {
    return false;
  }

  const code = error?.code;
  const reason = error?.rejectReason?.reason;

  return code === StakeErrorCode.BET_PRICE_CHANGED
    && reason === OperatorRefusedCode.READY_TO_ACCEPT_ONLY_AMOUNT;
};

export const selectOnlyAcceptAmountExpressError = createSelector([
  selectSingleBetsInfo,
  selectCheckedStakeType,
  selectAvailableStakeTypes,
],
(betsInfo, checkedStakeType, availableStakeTypes) => {
  const isExpress = checkedStakeType === StakeType.EXPRESS
      && availableStakeTypes.has(StakeType.EXPRESS);

  const isExpressError = isExpress
      && betsInfo.every((betInfo) => isOnlyAcceptAmount(betInfo.error));

  return isExpressError ? betsInfo.last()?.error : null;
},
);

export const selectIsOnlyAcceptAmountSingleError = createCachedSelector(
  (state: ReduxState, betId: number) => selectSingleBetsInfo(state).get(betId),
  selectCheckedStakeType,
  selectAvailableStakeTypes,
  (betInfo, checkedStakeType, availableStakeTypes) => {
    const isSingle = checkedStakeType === StakeType.SINGLE
      || !(availableStakeTypes.has(StakeType.EXPRESS));

    return isSingle && isOnlyAcceptAmount(betInfo?.error);
  },
)((_state: ReduxState, betId: number) => betId);

export const selectSingleAmountEntry: EntryAmountSelector = createSelector(
  selectSingleAmountModifier,
  (state: ReduxState) => selectStakeTypesState(state).amount,
  (state: ReduxState) => selectEntriesState(state).bets.size,
  (modifier, amount, numberOfEntries) => {
    let value = amount.numeric;

    if (Number.isNaN(value)) {
      return EntryAmount({ account: amount.account });
    }

    switch (modifier) {
      case AmountInputModifier.DIVIDE:
        if (numberOfEntries > 1) {
          value = roundAmount(value / numberOfEntries);
        }
        break;

      case AmountInputModifier.MULTIPLY:
        // do nothing with value
        break;

      default:
        console.error(`selectSingleAmountEntry -> unrecognized amount modifier [${modifier}]`);
        break;
    }

    return EntryAmount({
      value: value.toString(10),
      account: amount.account,
    });
  },
);

export const selectSinglesTotalAmount = createSelector(
  selectSingleBetsInfo,
  selectSingleAmountEntry,
  (state: ReduxState) => selectEntriesState(state).totalAmountActive,
  (betsInfo, singleEntryAmount, totalAmountActive) => {
    let result = 0;

    for (const entry of betsInfo.values()) {
      if (entry.freeBet || entry.pledgeStake) {
        continue;
      }

      const entryAmount = entry.get('amount');

      if (entryAmount === null) {
        console.error('selectSinglesTotalAmount -> `entryAmount` not found');
        return NaN;
      }

      const value = totalAmountActive
        ? singleEntryAmount.value
        : entryAmount.value;

      const amount = parseAmount(value);

      if (Number.isNaN(amount)) {
        return NaN;
      }

      result += amount;
    }

    return result;
  },
);

export const selectSinglesAccumulateAmount = createSelector(
  selectSingleBetsInfo,
  selectSingleAmountEntry,
  (state: ReduxState) => selectEntriesState(state).totalAmountActive,
  (betsInfo, singleEntryAmount, totalAmountActive) => {
    let result = 0;

    for (const entry of betsInfo.values()) {
      const entryAmount = entry.get('amount');

      if (entryAmount === null) {
        console.error('selectSinglesAccumulateAmount -> `entryAmount` not found');
        continue;
      }

      if (entry.pledgeStake || entry.freeBet) {
        continue;
      }

      const value = totalAmountActive
        ? singleEntryAmount.value
        : entryAmount.value;

      result += parseAmount(value) || 0;
    }

    return result;
  },
);

const getEntryValidation = (
  entry: RecordOfBetInfoType,
  singleEntryAmount: RecordOfEntryAmountType,
  totalAmountActive: boolean,
) => {
  const limits = entry.limits;

  if (!limits) {
    return AmountValidationResult.NO_ERRORS;
  }

  const entryAmount = entry.get('amount');

  if (entryAmount === null) {
    return AmountValidationResult.UNKNOWN;
  }

  const value = totalAmountActive
    ? singleEntryAmount.value
    : entryAmount.value;

  const amount = parseAmount(value);

  if (Number.isNaN(amount)) {
    return AmountValidationResult.EMPTY_VALUE;
  }

  if (amount < limits.min) {
    return AmountValidationResult.MIN_NOT_REACHED;
  }

  const pledge = entry.pledgeStake;

  if (pledge && (amount > pledge.maxWinning || amount > limits.max)) {
    return AmountValidationResult.MAX_EXCEEDED;
  }

  if (amount > limits.max) {
    return AmountValidationResult.BIG_STAKE;
  }

  if (entryAmount.account === AccountType.PLEDGE_STAKE_ACCOUNT && !pledge) {
    return AmountValidationResult.EMPTY_VALUE;
  }
};

export const selectEntryAmountValidation = createCachedSelector(
  (state: ReduxState, betId: number) => selectSingleBetsInfo(state).get(betId),
  selectSingleAmountEntry,
  (state: ReduxState) => selectEntriesState(state).totalAmountActive,
  (entry, singleEntryAmount, totalAmountActive) => {
    if (!entry) {
      return AmountValidationResult.UNKNOWN;
    }

    return getEntryValidation(entry, singleEntryAmount, totalAmountActive);
  },
)((_state: ReduxState, betId: number) => betId);

export const selectSingleEntryIdsWithTheLargestMinLimit = createSelector(
  selectSingleBetsInfo,
  selectSingleAmountEntry,
  (state: ReduxState) => selectEntriesState(state).totalAmountActive,
  (betsInfo, singleEntryAmount, totalAmountActive) => {
    let prevMinLimit = 0;
    const entryIds = new Set();

    for (const entry of betsInfo.values()) {
      const validation = getEntryValidation(entry, singleEntryAmount, totalAmountActive);

      if (validation === AmountValidationResult.MIN_NOT_REACHED) {
        const minLimit = entry.limits.min;

        if (minLimit > prevMinLimit) {
          prevMinLimit = minLimit;
          entryIds.clear();
          entryIds.add(entry.id);
        }
        else if (minLimit === prevMinLimit) {
          entryIds.add(entry.id);
        }
      }
    }

    return {
      limit: prevMinLimit,
      entryIds,
    };
  },
);

export const selectSingleAmountsValidation = createSelector(
  selectSingleBetsInfo,
  selectSingleAmountEntry,
  (state: ReduxState) => selectEntriesState(state).totalAmountActive,
  (betsInfo, singleEntryAmount, totalAmountActive) => {
    for (const entry of betsInfo.values()) {
      const validation = getEntryValidation(entry, singleEntryAmount, totalAmountActive);

      if (validation) {
        return validation;
      }
    }

    return AmountValidationResult.NO_ERRORS;
  },
);

export const selectSinglesMaxWinning = createSelector(
  (state: ReduxState) => selectEntriesState(state).bets,
  selectSingleBetsInfo,
  selectSingleAmountEntry,
  (state: ReduxState) => selectEntriesState(state).totalAmountActive,
  (entries, betsInfo, singleEntryAmount, totalAmountActive) => {
    return betsInfo.reduce((winning, entry) => {
      const entryAmount = entry.get('amount');

      if (entryAmount === null) {
        return winning;
      }

      const value = totalAmountActive
        ? singleEntryAmount.value
        : entryAmount.value;

      const amount = parseAmount(value);

      if (Number.isNaN(amount)) {
        return winning;
      }

      const price = entries.get(entry.id)?.price || 0;

      return winning + (amount * price);
    }, 0);
  },
);
